import React, { useEffect, useState } from "react";
import { Users, Plus, Pencil, Trash2, Phone, Loader2, Power, PowerOff } from "lucide-react";
import { toast } from "sonner";
import { api, BRL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import PageHeader from "@/components/PageHeader";

const EMPTY = { name: "", role: "", daily_rate: "", phone: "", active: true };

export default function Funcionarios() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/employees");
      setRows(r.data || []);
    } catch (e) {
      toast.error("Falha ao carregar funcionários");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditingId(null); setOpen(true); };
  const openEdit = (r) => {
    setForm({ name: r.name, role: r.role || "", daily_rate: String(r.daily_rate ?? ""), phone: r.phone || "", active: r.active !== false });
    setEditingId(r.id);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const rate = parseFloat(String(form.daily_rate).replace(",", "."));
    if (!form.name.trim()) { toast.error("Nome é obrigatório"); return; }
    if (!(rate >= 0)) { toast.error("Diária inválida"); return; }
    setSaving(true);
    try {
      const payload = { name: form.name, role: form.role, daily_rate: rate, phone: form.phone, active: form.active };
      if (editingId) await api.patch(`/employees/${editingId}`, payload);
      else await api.post("/employees", payload);
      toast.success(editingId ? "Funcionário atualizado" : "Funcionário cadastrado");
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (r) => {
    try {
      await api.patch(`/employees/${r.id}`, { active: !(r.active !== false) });
      await load();
    } catch (e) { toast.error("Falha ao atualizar"); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Remover ${r.name}? (as diárias ficarão preservadas)`)) return;
    try {
      await api.delete(`/employees/${r.id}`);
      toast.success("Removido");
      await load();
    } catch (e) { toast.error("Falha ao remover"); }
  };

  return (
    <div className="space-y-6">
      <PageHeader icon={Users} title="Funcionários" subtitle="Equipe fixa com valor-dia por funcionário" iconColor="bg-emerald-500" />

      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{rows.length} funcionário(s) cadastrado(s)</p>
        <Button onClick={openNew} className="bg-[#7c3aed] hover:bg-[#6d28d9] text-white" data-testid="emp-new-btn">
          <Plus className="w-4 h-4 mr-1" /> Novo funcionário
        </Button>
      </div>

      {loading ? (
        <div className="h-40 flex items-center justify-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 p-10 text-center">
          <Users className="w-10 h-10 text-slate-300 mx-auto" />
          <p className="mt-3 font-heading font-bold text-slate-700">Nenhum funcionário ainda</p>
          <p className="text-xs text-slate-500 mt-1">Clique em "Novo funcionário" para começar.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map((r) => {
            const active = r.active !== false;
            return (
              <article
                key={r.id}
                data-testid={`emp-card-${r.id}`}
                className={`rounded-2xl border p-4 bg-white ${active ? "border-slate-100 card-shadow" : "border-dashed border-slate-200 opacity-70"}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-11 h-11 rounded-full flex items-center justify-center text-white font-black ${active ? "bg-gradient-to-br from-[#1e3a8a] to-[#7c3aed]" : "bg-slate-400"}`}>
                    {r.name[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-heading font-black text-[#1e3a8a] leading-tight truncate">{r.name}</p>
                    <p className="text-xs text-slate-500 truncate">{r.role || "—"}</p>
                    {r.phone && (
                      <p className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1"><Phone className="w-3 h-3" /> {r.phone}</p>
                    )}
                  </div>
                </div>
                <div className="mt-3 flex items-end justify-between">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Diária</p>
                    <p className="font-black text-slate-900">{BRL(r.daily_rate || 0)}</p>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => toggleActive(r)}
                      data-testid={`emp-toggle-${r.id}`}
                      className={`p-2 rounded-lg ${active ? "text-emerald-600 hover:bg-emerald-50" : "text-slate-400 hover:bg-slate-50"}`}
                      title={active ? "Desativar" : "Ativar"}
                    >
                      {active ? <Power className="w-4 h-4" /> : <PowerOff className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => openEdit(r)}
                      data-testid={`emp-edit-${r.id}`}
                      className="p-2 rounded-lg text-[#1e3a8a] hover:bg-slate-100"
                      title="Editar"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => remove(r)}
                      data-testid={`emp-delete-${r.id}`}
                      className="p-2 rounded-lg text-rose-600 hover:bg-rose-50"
                      title="Remover"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="emp-form-dialog" className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-[#1e3a8a] font-black">
            {editingId ? "Editar funcionário" : "Novo funcionário"}
          </DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Nome *</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="emp-name" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Função</label>
              <Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="Ex.: Pintor, Pedreiro, Eletricista" data-testid="emp-role" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Diária (R$) *</label>
                <Input type="text" inputMode="decimal" value={form.daily_rate} onChange={(e) => setForm({ ...form, daily_rate: e.target.value })} placeholder="150,00" data-testid="emp-rate" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Telefone</label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(11) 90000-0000" data-testid="emp-phone" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="emp-active" />
              Ativo
            </label>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-[#7c3aed] hover:bg-[#6d28d9] text-white" data-testid="emp-submit">
                {saving ? "Salvando..." : editingId ? "Salvar alterações" : "Cadastrar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
